import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from '@bonusly/common-user-popover';
import { Button, Divider, Grid, GridItem, PopoverBody, Skeleton, Stack, Text, theme, Tooltip } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useUsers } from '@bonusly/user-queries';

import { GroupAvatar, isGroupAvatarType } from '../../../groups/group-avatar';

interface GroupPopoverContentProps {
	group: Recipient;
	isExpandable?: boolean;
	onExpand?: (users: Recipient[]) => void;
}

const MAX_USERS_TO_DISPLAY = 20;

export const GroupPopoverContent: FC<GroupPopoverContentProps> = ({ group, isExpandable, onExpand }) => {
	const { data: users, isLoading } = useUsers(group.userIds);
	const { t } = useTranslation('components', { keyPrefix: 'group_popover' });

	const [expanded, setExpanded] = useState(false);

	const userNames = useMemo(() => {
		return users?.map((u) => u.displayName) || [];
	}, [users]);

	const recipients: Recipient[] = useMemo(() => {
		if (!onExpand || !users || users.length < 1) {
			return [];
		}

		return users.map((u) => {
			return new Recipient({
				id: u.id,
				display_name: u.displayName,
				profile_pic_url: u.profilePic,
				username: u.username,
				user_mode: u.userMode,
			});
		});
	}, [users, onExpand]);

	const namesToBeDisplayed = useMemo(() => {
		if (expanded) {
			return userNames;
		}
		return userNames.slice(0, MAX_USERS_TO_DISPLAY);
	}, [userNames, expanded]);

	const overflowNames = useMemo(() => {
		if (expanded) {
			return [];
		}
		return userNames.slice(MAX_USERS_TO_DISPLAY);
	}, [userNames, expanded]);

	const showOverflow = useCallback(() => {
		setExpanded(true);
	}, []);

	const onClick = useMemo(() => {
		if (onExpand) {
			return () => onExpand(recipients);
		} else {
			return () => undefined;
		}
	}, [onExpand, recipients]);

	const isEveryone = group.dimension === 'everyone';
	const groupTypeText = t(group.dimension);

	return (
		<PopoverBody paddingX={0} paddingTop={5} paddingBottom={isExpandable ? 2 : 5} zIndex={theme.zIndices.tooltip + 200}>
			<Stack spacing={3}>
				<Grid
					alignItems="center"
					templateColumns="56px 1fr 60px"
					templateRows="minmax(28px, max-content) minmax(26px, max-content)"
					overflow="wrap"
					paddingX={5}
					w="100%"
				>
					<GridItem colSpan={1} rowSpan={2}>
						{isGroupAvatarType(group.dimension) ? (
							<GroupAvatar groupType={group.dimension} size="xl" boxShadow="0 0 0 2px var(--chakra-colors-bg-surface)" />
						) : (
							<UserAvatar
								bg="transparent"
								boxShadow="0 0 0 2px var(--chakra-colors-bg-surface)"
								size="xl"
								user={{
									displayName: group.displayName,
									profilePic: group.profilePic,
								}}
							/>
						)}
					</GridItem>
					<GridItem colSpan={2} pl="8px" pt="2px" textOverflow="wrap">
						<Text size="lg" fontWeight="medium" textTransform="capitalize">
							{group.displayName}
						</Text>
					</GridItem>
					<GridItem pl="8px" pb="2px" textOverflow="wrap" maxW="162px">
						{isLoading ? (
							<Skeleton height={4} />
						) : (
							<Text flex={1} fontSize="xs" fontWeight="normal" color="text.softer">
								{isEveryone ? '' : `${groupTypeText}  • `}
								{users?.length} {t('people')}
							</Text>
						)}
					</GridItem>
				</Grid>

				{!isEveryone && (
					<>
						<Stack paddingX={5}>
							<Divider />
						</Stack>
						<Stack overflowY="auto" maxH={expanded ? 80 : 'unset'} paddingX={5}>
							{isLoading ? (
								<>
									<Skeleton height={4} mt={2} />
									<Skeleton height={4} />
								</>
							) : (
								<Text size="sm">
									{namesToBeDisplayed.length !== userNames.length && !expanded
										? namesToBeDisplayed.join(', ')
										: t('users_list', {
												users: namesToBeDisplayed,
												count: userNames.length,
											})}
									{overflowNames.length > 0 && (
										<Text as="span" color="primary.fg" cursor="pointer" size="sm" onClick={showOverflow}>
											{' '}
											{t('list_overflow', { count: overflowNames.length })}
										</Text>
									)}
								</Text>
							)}
						</Stack>
					</>
				)}

				{isExpandable && (
					<Tooltip label={t('expand_group_tooltip')} hasArrow>
						<Button colorScheme="primary" isDisabled={isLoading} marginX={2} onClick={onClick} size="sm" variant="alpha">
							{t('expand_group')}
						</Button>
					</Tooltip>
				)}
			</Stack>
		</PopoverBody>
	);
};
